import { Container, Text, Title } from '@mantine/core';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { RequireAuth } from './components/RequireAuth';
import { PublicLayout } from './components/PublicLayout';
import { AcceptInvite } from './pages/AcceptInvite';
import { ApiKeys } from './pages/ApiKeys';
import { Billing } from './pages/Billing';
import { BrandSettings } from './pages/BrandSettings';
import { Brands } from './pages/Brands';
import { ChooseFormat } from './pages/ChooseFormat';
import { Developers, OrgWebhook } from './pages/Developers';
import { DraftInteraction } from './pages/DraftInteraction';
import { EmailLists } from './pages/EmailLists';
import { ForgotPassword } from './pages/ForgotPassword';
import { InteractionEditor } from './pages/InteractionEditor';
import { InteractionList } from './pages/InteractionList';
import { Landing } from './pages/Landing';
import { Lists } from './pages/Lists';
import { Login } from './pages/Login';
import { NewInteraction } from './pages/NewInteraction';
import { NewsletterSync } from './pages/NewsletterSync';
import { OrgConsent, OrgTheme } from './pages/OrgDefaults';
import { ResetPassword } from './pages/ResetPassword';
import { Security } from './pages/Security';
import { Settings } from './pages/Settings';
import { Signup } from './pages/Signup';
import { Subscribers } from './pages/Subscribers';
import { Team } from './pages/Team';
import { VerifyEmail } from './pages/VerifyEmail';

/** The route tree on its own, so tests can mount it inside a memory router. */
export function AppRoutes() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route element={<RequireAuth />}>
        <Route path="/interactions" element={<InteractionList />} />
        <Route path="/interactions/new" element={<ChooseFormat />} />
        {/* Before the :format route, and matched ahead of it: 'draft' names a way in, not a format. */}
        <Route path="/interactions/new/draft" element={<DraftInteraction />} />
        <Route path="/interactions/new/:format" element={<NewInteraction />} />
        <Route path="/interactions/:id" element={<InteractionEditor />} />
        <Route path="/newsletters" element={<EmailLists />}>
          {/* Subscribers is what the address has always shown, so bookmarks still land here. */}
          <Route index element={<Subscribers />} />
          <Route path="lists" element={<Lists />} />
          <Route path="mailchimp" element={<NewsletterSync />} />
        </Route>

        <Route path="/settings" element={<Settings />}>
          {/* Settings is a place rather than a page, so it opens on the first section in the list. */}
          <Route index element={<Navigate to="/settings/brands" replace />} />
          <Route path="brands" element={<Brands />} />
          <Route path="brands/:brandId" element={<BrandSettings />} />
          <Route path="themes" element={<OrgTheme />} />
          <Route path="consent" element={<OrgConsent />} />
          <Route path="team" element={<Team />} />
          {/* Reachable by anybody who types it. The screen itself, and the API, are owners only. */}
          <Route path="security" element={<Security />} />
          <Route path="billing" element={<Billing />} />
          <Route path="developers" element={<Developers />}>
            {/* The keys are what most people come here for, so they are what the tab opens on. */}
            <Route index element={<ApiKeys />} />
            <Route path="webhook" element={<OrgWebhook />} />
          </Route>
        </Route>

        {/*
         * Where these screens used to live. Editors bookmark, and a link to a brand goes in an
         * email as often as it goes in the address bar, so the old addresses keep working rather
         * than landing somebody on a page that says the address does not exist.
         */}
        <Route path="/subscribers" element={<Navigate to="/newsletters" replace />} />
        <Route path="/brands" element={<Navigate to="/settings/brands" replace />} />
        <Route path="/brands/:brandId" element={<MovedBrand />} />
        <Route path="/team" element={<Navigate to="/settings/team" replace />} />
        <Route path="/usage" element={<Navigate to="/settings/billing" replace />} />
        <Route path="/api-keys" element={<Navigate to="/settings/developers" replace />} />
      </Route>
    </Routes>
  );
}

/** The old address for one brand, carrying its id to the new one. */
function MovedBrand() {
  const { brandId } = useParams<{ brandId: string }>();
  if (brandId === undefined) return <Navigate to="/settings/brands" replace />;
  return <Navigate to={`/settings/brands/${brandId}`} replace />;
}

function NotFound() {
  return (
    <Container component="main" size={460} pt={80} pb={64}>
      <Title order={1}>Page not found</Title>
      <Text mt="sm">
        That address does not exist. Check the link, or start from your interactions.
      </Text>
    </Container>
  );
}
